import React, { useState, useMemo } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { submitQualifiedInvestorAttestation } from '../../services/fundInvestorsService';

const NI_45_106_FR_URL =
  'https://lautorite.qc.ca/fileadmin/lautorite/reglementation/valeurs-mobilieres/45-106/2025-09-19/2025sept19-45-106-vofficielle-fr.pdf';
const NI_45_106_EN_URL =
  'https://lautorite.qc.ca/fileadmin/lautorite/reglementation/valeurs-mobilieres/45-106/2025-09-19/2025sept19-45-106-vofficielle-en.pdf';

/**
 * Single-placement qualified investor self-attestation (NI 45-106).
 * Caller must ensure the investing holder (individual vs company) is saved on the fundInvestor doc before enabling.
 */
export default function PlacementQualifiedInvestorAttestation({ fundInvestorId, t, disabled, disabledHint, hideHeader }) {
  const [category, setCategory] = useState('accredited');
  const [confirmQualification, setConfirmQualification] = useState(false);
  const [confirmNotAdvice, setConfirmNotAdvice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const categoryExplanation = useMemo(() => {
    if (category === 'accredited') return t('fundInvestors.qualified.categoryExplanationAccredited');
    if (category === 'eligible') return t('fundInvestors.qualified.categoryExplanationEligible');
    return t('fundInvestors.qualified.categoryExplanationInstitution');
  }, [category, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (disabled) {
      setError(disabledHint || t('fundInvestors.qualified.saveCompanyBeforeAttest'));
      return;
    }
    if (!confirmQualification || !confirmNotAdvice) {
      setError(t('fundInvestors.qualified.errorConfirm'));
      return;
    }
    setSubmitting(true);
    try {
      await submitQualifiedInvestorAttestation(fundInvestorId, {
        category,
        confirmQualification: true,
        confirmNotAdvice: true,
      });
      setConfirmQualification(false);
      setConfirmNotAdvice(false);
    } catch (err) {
      const code = err?.code || err?.message;
      if (code === 'EMAIL_MISMATCH' || err?.message === 'EMAIL_MISMATCH') {
        setError(t('fundInvestors.qualified.errorEmailMismatch'));
      } else if (code === 'HOLDER_COMPANY_REQUIRED' || err?.message === 'HOLDER_COMPANY_REQUIRED') {
        setError(t('fundInvestors.qualified.errorHolderCompanyRequired'));
      } else if (code === 'HOLDER_NOT_COMMITTED' || err?.message === 'HOLDER_NOT_COMMITTED') {
        setError(t('investorFunds.attestRequiresHolderSave'));
      } else {
        setError(err?.message || t('fundInvestors.qualified.errorGeneric'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={
        hideHeader
          ? 'rounded-lg border border-amber-200 bg-white/95 shadow-sm overflow-hidden'
          : 'rounded-xl border-2 border-amber-200 bg-amber-50/80 shadow-sm overflow-hidden'
      }
    >
      {!hideHeader && (
        <div className="px-4 py-3 border-b border-amber-200/80 bg-amber-100/50 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-800 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-base font-semibold text-amber-950">{t('fundInvestors.qualified.title')}</h3>
            <p className="text-sm text-amber-900/90 mt-1">{t('fundInvestors.qualified.intro')}</p>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="p-4 sm:p-5 space-y-4 bg-white/90">
        {hideHeader && (
          <p className="text-sm text-gray-700 leading-relaxed">{t('fundInvestors.qualified.intro')}</p>
        )}
        {disabled && (
          <p className="text-sm text-amber-900 bg-amber-100/80 border border-amber-200 rounded-lg px-3 py-2">
            {disabledHint || t('fundInvestors.qualified.saveCompanyBeforeAttest')}
          </p>
        )}

        <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
          <p className="font-medium text-gray-800">{t('fundInvestors.qualified.ni45106Title')}</p>
          <p className="text-xs text-gray-600 mt-1">{t('fundInvestors.qualified.ni45106Intro')}</p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <a
              href={NI_45_106_FR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-indigo-700 font-medium hover:underline"
            >
              {t('fundInvestors.qualified.ni45106Fr')}
            </a>
            <a
              href={NI_45_106_EN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-indigo-700 font-medium hover:underline"
            >
              {t('fundInvestors.qualified.ni45106En')}
            </a>
          </div>
        </div>

        <div>
          <label htmlFor={`qualified-category-${fundInvestorId}`} className="block text-sm font-medium text-gray-800 mb-1">
            {t('fundInvestors.qualified.categoryLabel')}
          </label>
          <select
            id={`qualified-category-${fundInvestorId}`}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={disabled}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-50"
          >
            <option value="accredited">{t('fundInvestors.qualified.categoryAccredited')}</option>
            <option value="eligible">{t('fundInvestors.qualified.categoryEligible')}</option>
            <option value="institution">{t('fundInvestors.qualified.categoryInstitution')}</option>
          </select>
        </div>

        <div className="rounded-lg border border-gray-200 bg-slate-50/70 px-3 py-2 text-sm text-gray-800">
          <p className="font-medium text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-indigo-600 shrink-0" />
            {t('fundInvestors.qualified.categoryExplanationTitle')}
          </p>
          <p className="text-sm text-gray-700 mt-1">{categoryExplanation}</p>
        </div>

        <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmQualification}
            onChange={(e) => setConfirmQualification(e.target.checked)}
            disabled={disabled}
            className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
          />
          <span>{t('fundInvestors.qualified.checkboxQualification')}</span>
        </label>

        <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmNotAdvice}
            onChange={(e) => setConfirmNotAdvice(e.target.checked)}
            disabled={disabled}
            className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
          />
          <span>{t('fundInvestors.qualified.checkboxNotAdvice')}</span>
        </label>

        <p className="text-xs text-gray-500 border-t border-gray-100 pt-3">{t('fundInvestors.qualified.disclaimer')}</p>

        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
        )}

        <button
          type="submit"
          disabled={submitting || disabled}
          className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
        >
          {submitting ? t('fundInvestors.qualified.submitting') : t('fundInvestors.qualified.submitSinglePlacement')}
        </button>
      </form>
    </div>
  );
}
